import React, { ReactNode, MouseEvent } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { scrollToSectionWhenReady } from 'components/document/helpers/functions'

interface SmartLinkProps {
  href?: string
  children: ReactNode
  target?: string
  rel?: string
  className?: string
}

const parseSameDocumentSectionId = (href: string | undefined, currentPathname: string): string | null => {
  if (!href) return null
  try {
    const url = new URL(href, window.location.origin)
    if (url.origin !== window.location.origin) return null
    if (url.pathname !== currentPathname) return null
    return url.searchParams.get('section')
  } catch {
    return null
  }
}

export const SmartLink: React.FC<SmartLinkProps> = ({ href, children, target, rel, className }: SmartLinkProps) => {
  const location = useLocation()
  const [, setSearchParams] = useSearchParams()
  const sectionId = parseSameDocumentSectionId(href, location.pathname)

  if (sectionId) {
    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
      event.preventDefault()
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('section', sectionId)
          return next
        },
        { replace: false }
      )
      scrollToSectionWhenReady(sectionId)
    }

    return (
      <a href={href} className={className} onClick={handleClick}>
        {children}
      </a>
    )
  }

  return (
    <a href={href} target={target ?? '_blank'} rel={rel ?? 'noopener noreferrer'} className={className}>
      {children}
    </a>
  )
}

export default SmartLink
